import React from 'react';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { cn } from '@/lib/utils';
import { useClientStatusTags } from '@/hooks/useClientStatusTags';

// Поддержка legacy типов для обратной совместимости
export type LoyaltyStatus = 'white' | 'gray' | 'black';

interface LoyaltyStatusSelectorProps {
  value?: LoyaltyStatus | string | null; // Поддержка как legacy статусов, так и ID тэгов
  valueId?: string | null; // Новый способ через ID тэга
  onValueChange?: (status: LoyaltyStatus) => void; // Legacy коллбэк
  onValueIdChange?: (statusId: string | null) => void; // Новый коллбэк для ID тэга
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  useTags?: boolean; // Флаг для принудительного использования новой системы тэгов
}

/**
 * Унифицированный селектор статуса лояльности клиента
 * Поддерживает как legacy статусы (white/gray/black), так и новые тэги из централизованной системы
 * Использует эмодзи-обозначения для улучшенного UX:
 * ✅ - белый список (высокая лояльность)
 * ⚠️ - серый список (средняя лояльность) 
 * 💔 - черный список (низкая лояльность)
 */
export const LoyaltyStatusSelector: React.FC<LoyaltyStatusSelectorProps> = ({
  value,
  valueId,
  onValueChange,
  onValueIdChange,
  disabled = false,
  size = 'md',
  className,
  useTags = false
}) => {
  const { statusTags, statusOptions: tagOptions, loading } = useClientStatusTags();

  // Определяем, используется ли новая система тэгов
  const isUsingTags = useTags || valueId !== undefined || onValueIdChange !== undefined || 
    (!value || !['white', 'gray', 'black'].includes(value as string));

  // Legacy опции для обратной совместимости
  const legacyStatusOptions = [
    {
      value: 'white' as const,
      emoji: '✅',
      label: 'Белый',
      description: 'Высокая лояльность',
      bgClass: 'data-[state=on]:bg-green-50 data-[state=on]:border-green-500'
    },
    {
      value: 'gray' as const,
      emoji: '⚠️',
      label: 'Серый',
      description: 'Средняя лояльность',
      bgClass: 'data-[state=on]:bg-yellow-50 data-[state=on]:border-yellow-500'
    },
    {
      value: 'black' as const,
      emoji: '💔',
      label: 'Черный',
      description: 'Низкая лояльность',
      bgClass: 'data-[state=on]:bg-red-50 data-[state=on]:border-red-500'
    }
  ];

  // Формируем опции для отображения
  const displayOptions = isUsingTags ? 
    statusTags.map(tag => ({
      value: tag.id,
      emoji: tag.color === 'green' || tag.name.toLowerCase().includes('белый') ? '✅' :
             tag.color === 'red' || tag.name.toLowerCase().includes('черн') ? '💔' : '⚠️',
      label: tag.name,
      description: tag.description || tag.name,
      bgClass: tag.color === 'green' ? 'data-[state=on]:bg-green-50 data-[state=on]:border-green-500' :
               tag.color === 'red' ? 'data-[state=on]:bg-red-50 data-[state=on]:border-red-500' :
               'data-[state=on]:bg-yellow-50 data-[state=on]:border-yellow-500'
    })) :
    legacyStatusOptions;

  const sizeClasses = {
    sm: {
      button: 'h-8 w-8',
      emoji: 'text-sm',
      label: 'text-xs'
    },
    md: {
      button: 'h-10 w-10',
      emoji: 'text-base',
      label: 'text-xs'
    },
    lg: {
      button: 'h-12 w-12',
      emoji: 'text-lg',
      label: 'text-sm'
    }
  };

  // Показываем загрузку для новой системы тэгов
  if (isUsingTags && loading) {
    return (
      <div className={cn('flex justify-start gap-4', className)}>
        <div className="text-sm text-gray-500">Загрузка статусов...</div>
      </div>
    );
  }

  const currentValue = isUsingTags ? valueId : value;

  return (
    <ToggleGroup 
      type="single" 
      value={currentValue || ''} 
      onValueChange={(newValue) => {
        if (newValue && !disabled) {
          if (isUsingTags) {
            onValueIdChange?.(newValue);
          } else {
            onValueChange?.(newValue as LoyaltyStatus);
          }
        }
      }}
      disabled={disabled}
      className={cn('flex justify-start gap-4', className)}
    >
      {displayOptions.map((option) => (
        <div key={option.value} className="flex flex-col items-center gap-1">
          <ToggleGroupItem 
            value={option.value}
            aria-label={`${option.label} список - ${option.description}`}
            title={`${option.label} список - ${option.description}`}
            className={cn(
              sizeClasses[size].button,
              'rounded-full bg-white border border-gray-300',
              option.bgClass,
              'flex items-center justify-center p-0',
              'hover:bg-gray-50',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
            disabled={disabled}
          >
            <span 
              className={cn(sizeClasses[size].emoji)}
              role="img" 
              aria-label={option.description}
            >
              {option.emoji}
            </span>
          </ToggleGroupItem>
          <span className={cn(
            sizeClasses[size].label,
            'text-gray-600 font-medium',
            disabled && 'text-gray-400'
          )}>
            {option.label}
          </span>
        </div>
      ))}
    </ToggleGroup>
  );
};

export default LoyaltyStatusSelector;
